/**
 * Nudging a placed piece about its own hole, not about the model container's
 * origin.
 *
 * `placeOnBody` moves the model CONTAINER (`MODEL_ROOT_NODE_ID`), and the Adjust
 * rows read and write that node through `getNodeDetails` / `setNodeTransform`.
 * But the container's origin is wherever the file put it — usually the bounding
 * box centre, which on a solitaire sits millimetres up toward the head. Rotating
 * or scaling about it swings the band off the finger. So every rotation and
 * size edit here keeps one model-local point fixed in the world: the hole
 * centre, read once after placement (`getModelFit().holeCenter`).
 *
 * The container's parent is the scene, so its local transform IS its world
 * transform, and three's default Euler order is XYZ (matrix = Rx · Ry · Rz) —
 * the order `getNodeDetails` reports in. Pure: no three.js in this repo.
 */
import type { Vec3 } from "../../../shared/api-contract";

/** Row-major 3x3. */
export type Mat3 = [number, number, number, number, number, number, number, number, number];

export interface NodeTransform {
  position: Vec3;
  rotationDeg: Vec3;
  scale: Vec3;
}

const D2R = Math.PI / 180;

export function mul(a: Mat3, b: Mat3): Mat3 {
  const o = new Array(9).fill(0) as Mat3;
  for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) {
    o[r * 3 + c] = a[r * 3] * b[c] + a[r * 3 + 1] * b[3 + c] + a[r * 3 + 2] * b[6 + c];
  }
  return o;
}

export const apply = (m: Mat3, v: Vec3): Vec3 => ({
  x: m[0] * v.x + m[1] * v.y + m[2] * v.z,
  y: m[3] * v.x + m[4] * v.y + m[5] * v.z,
  z: m[6] * v.x + m[7] * v.y + m[8] * v.z,
});

export const transpose = (m: Mat3): Mat3 => [m[0], m[3], m[6], m[1], m[4], m[7], m[2], m[5], m[8]];

/** Rotation about one world axis, degrees. */
export function axisRotation(axis: "x" | "y" | "z", deg: number): Mat3 {
  const c = Math.cos(deg * D2R), s = Math.sin(deg * D2R);
  // Snap the quarter turns the Rotate buttons make, so 4 × 90° is exact.
  const q = (v: number) => (Math.abs(v) < 1e-12 ? 0 : Math.abs(Math.abs(v) - 1) < 1e-12 ? Math.sign(v) : v);
  const C = q(c), S = q(s);
  if (axis === "x") return [1, 0, 0, 0, C, -S, 0, S, C];
  if (axis === "y") return [C, 0, S, 0, 1, 0, -S, 0, C];
  return [C, -S, 0, S, C, 0, 0, 0, 1];
}

/** three's Euler 'XYZ' → matrix (Rx · Ry · Rz). */
export function eulerToMatrix(deg: Vec3): Mat3 {
  return mul(mul(axisRotation("x", deg.x), axisRotation("y", deg.y)), axisRotation("z", deg.z));
}

/** Matrix → three's Euler 'XYZ', degrees (`Euler.setFromRotationMatrix`). */
export function matrixToEuler(m: Mat3): Vec3 {
  const m11 = m[0], m12 = m[1], m13 = m[2];
  const m22 = m[4], m23 = m[5];
  const m32 = m[7], m33 = m[8];
  const y = Math.asin(Math.max(-1, Math.min(1, m13)));
  let x: number, z: number;
  if (Math.abs(m13) < 0.9999999) {
    x = Math.atan2(-m23, m33);
    z = Math.atan2(-m12, m11);
  } else {
    x = Math.atan2(m32, m22);
    z = 0;
  }
  const r = (v: number) => {
    const d = v / D2R;
    // -0 and 179.99999999 are noise in a number field.
    const k = Math.round(d * 1e6) / 1e6;
    return Object.is(k, -0) ? 0 : k;
  };
  return { x: r(x), y: r(y), z: r(z) };
}

/** The world position of a container-local point. */
export function worldOf(t: NodeTransform, local: Vec3): Vec3 {
  const p = apply(eulerToMatrix(t.rotationDeg), { x: t.scale.x * local.x, y: t.scale.y * local.y, z: t.scale.z * local.z });
  return { x: t.position.x + p.x, y: t.position.y + p.y, z: t.position.z + p.z };
}

/** The container-local coordinates of a world point. */
export function localOf(t: NodeTransform, world: Vec3): Vec3 {
  const d = { x: world.x - t.position.x, y: world.y - t.position.y, z: world.z - t.position.z };
  const p = apply(transpose(eulerToMatrix(t.rotationDeg)), d);
  const div = (a: number, s: number) => (s !== 0 ? a / s : 0);
  return { x: div(p.x, t.scale.x), y: div(p.y, t.scale.y), z: div(p.z, t.scale.z) };
}

/**
 * The transform `next` describes (a new rotation and/or scale), with the
 * position moved so `pivotLocal` stays where it is in the world. Position in
 * `next` is ignored: a translation has no pivot.
 */
export function aboutPivot(cur: NodeTransform, pivotLocal: Vec3, next: { rotationDeg?: Vec3; scale?: Vec3 }): NodeTransform {
  const rotationDeg = next.rotationDeg ?? cur.rotationDeg;
  const scale = next.scale ?? cur.scale;
  const c = worldOf(cur, pivotLocal);
  const p = apply(eulerToMatrix(rotationDeg), { x: scale.x * pivotLocal.x, y: scale.y * pivotLocal.y, z: scale.z * pivotLocal.z });
  return { position: { x: c.x - p.x, y: c.y - p.y, z: c.z - p.z }, rotationDeg, scale };
}

/** A quarter (or any) turn about a WORLD axis — under On Body's fixed front
 *  camera, the screen's axes: X across, Y up, Z toward the viewer. */
export function turnAboutWorldAxis(cur: NodeTransform, pivotLocal: Vec3, axis: "x" | "y" | "z", deg: number): NodeTransform {
  const R = mul(axisRotation(axis, deg), eulerToMatrix(cur.rotationDeg));
  return aboutPivot(cur, pivotLocal, { rotationDeg: matrixToEuler(R) });
}
